/**
 * Field Scope Store
 *
 * 管理案場相關狀態（組織、案場、過濾器等）
 */

import { defineStore } from 'pinia'
import { watch, type App, type Component } from 'vue'
import accessService from '@shared/services/auth/accessService'
import { authService } from '@shared/services/auth/authService'
import { eventBus, globalStateData } from '@shared/stores/globalState'

// ==================== 類型定義 ====================

/** 參數配置項目 */
export interface ParamConfigItem {
  stateKey: string | null
  storageKey: string
  hashKey: string
  defaultValue: string | boolean
}

/** 參數配置類型 */
export type ParamConfig = {
  [key: string]: ParamConfigItem
}

/** 緩存配置項目 */
export interface CacheConfigItem {
  key: string
  expiry: number
}

/** 案場類型過濾選項 */
export interface FilterTypeItem {
  title: string
  key: string
  class?: string
}

/** 組織資料 */
export interface Organization {
  id: string
  name: string
  focus_fields?: string[]
  [key: string]: unknown
}

/** 案場位置 */
export interface FieldLocation {
  latitude?: number | string | null
  longitude?: number | string | null
}

/** 案場資料 */
export interface Field {
  id: string
  name?: string
  address?: string
  city?: string
  type?: string[]
  status?: string
  location?: FieldLocation | null
  latitude?: number | null
  longitude?: number | null
  [key: string]: unknown
}

/** 關係資料 */
export interface Relationships {
  users: Record<string, unknown>
  organizations: Record<string, unknown>
  fields: Record<string, unknown>
}

/** 城市選項 */
export interface CityOption {
  name: string
  available: boolean
}

/** 地圖目標點 */
export interface MapTargetPoint {
  lat: number
  lng: number
}

/** Field State 介面 */
export interface FieldStateType {
  userNameDict: Record<string, string>
  filterTypeList: FilterTypeItem[]
  selectFilter: string
  selectFilterKey: string
  selectCity: string
  selectTarget: string
  searchKeyword: string
  displayAreaInfo: boolean
  fieldIds: string[]
  relationships: Relationships
  organizations: Organization[]
  fields: Field[]
  selectedOrgId: string
  selectedOrgName?: string
  selectedFieldId: string
  dataFieldWithType: Field[]
  dataCity: (string | CityOption)[]
  dataField: Field[]
  selectFieldsCount: number
  disableFieldSelector: boolean
  disableSelectorBox: boolean
  pageName: string
  mainMenuName: string
  dynamicComponents: Map<string, Component>
  dataIsLoaded: boolean
  focusFields: string[]
  mapLastTargetPoint?: MapTargetPoint
  invite?: string
  getSelectorState?: () => { selectFilter: string }
}

// ==================== 常數配置 ====================

export const PARAM_CONFIG: ParamConfig = {
  orgId: {
    stateKey: 'selectedOrgId',
    storageKey: 'currentOrgId',
    hashKey: 'o',
    defaultValue: '',
  },
  fieldId: {
    stateKey: 'selectedFieldId',
    storageKey: 'currentFieldId',
    hashKey: 'f',
    defaultValue: '',
  },
  filter: {
    stateKey: null,
    storageKey: 'view_selectFilter',
    hashKey: 'filter',
    defaultValue: '全部類型',
  },
  city: {
    stateKey: null,
    storageKey: 'view_selectCity',
    hashKey: 'c',
    defaultValue: '全部地區',
  },
  target: {
    stateKey: null,
    storageKey: 'view_selectTarget',
    hashKey: 't',
    defaultValue: '全部案場',
  },
  keyword: {
    stateKey: null,
    storageKey: 'view_searchKeyword',
    hashKey: 'k',
    defaultValue: '',
  },
  mainMenu: {
    stateKey: 'mainMenuName',
    storageKey: 'view_mainMenuName',
    hashKey: 'm',
    defaultValue: '',
  },
  invite: {
    stateKey: 'invite',
    storageKey: 'invite',
    hashKey: 'invite',
    defaultValue: '',
  },
}

export const CACHE_CONFIG: Record<string, CacheConfigItem> = {
  fields: {
    key: 'cached_fields',
    expiry: 5 * 60 * 1000
  }
}

const TAIWAN_CITIES = [
  '台北市', '新北市', '基隆市', '桃園市', '新竹市', '新竹縣', '苗栗縣',
  '台中市', '台中縣', '彰化縣', '南投縣', '雲林縣', '嘉義市', '嘉義縣',
  '台南市', '高雄市', '屏東縣',
  '宜蘭縣', '花蓮縣', '台東縣', '澎湖縣', '金門縣', '連江縣',
]

// ==================== 輔助函數 ====================

/**
 * 將「臺」轉為「台」
 */
const normalizeCity = (cityName: string | null | undefined): string => {
  return cityName ? cityName.replace(/臺/g, '台') : ''
}

// ==================== Store 定義 ====================

export const fieldState = defineStore('fieldState', {
  state: (): FieldStateType => ({
    // 案場相關資料
    userNameDict: {},
    filterTypeList: [
      { 'title': '全部類型', 'key': '' },
      { 'title': '焦點案場', 'key': 'FOCUS', 'class': 'm1' },
      { 'title': '問題案場', 'key': 'ERROR', 'class': 'm1' },
      { 'title': '監控案場', 'key': 'MONITOR', 'class': 'm1' },
      { 'title': '複合案場', 'key': 'VPP', 'class': '' },
      { 'title': '光電案場', 'key': 'PV', 'class': '' },
      { 'title': '生質能案場', 'key': 'BG', 'class': '' },
      { 'title': '熱泵案場', 'key': 'HP', 'class': '' },
      { 'title': '儲能案場', 'key': 'ES', 'class': '' },
      { 'title': '其它案場', 'key': 'OT', 'class': '' },
      { 'title': '第三方案場', 'key': 'THIRD', 'class': 'm1' },
    ],
    selectFilter: '監控案場',
    selectFilterKey: 'MONITOR',
    selectCity: '全部地區',
    selectTarget: '全部案場',
    searchKeyword: '',
    displayAreaInfo: false,
    fieldIds: [],
    relationships: { users: {}, organizations: {}, fields: {} },
    // 從 localStorage 載入緩存的組織列表（避免抽屜組織選擇空白）
    organizations: JSON.parse(localStorage.getItem('cached_organizations') || '[]'),
    fields: [],
    selectedOrgId: '',
    selectedFieldId: '',
    dataFieldWithType: [],
    dataCity: [],
    dataField: [],
    selectFieldsCount: 0,
    disableFieldSelector: false,
    disableSelectorBox: false,
    pageName: '',
    mainMenuName: '',
    dynamicComponents: new Map(),
    dataIsLoaded: false,
    focusFields: [],
  }),

  getters: {
    /**
     * 取得當前組織
     */
    currentOrg(): Organization | undefined {
      return this.organizations.find(x => x.id === this.selectedOrgId)
    },

    /**
     * 取得當前案場
     */
    currentField(): Field | null {
      if (!this.selectedFieldId || !this.fields || !Array.isArray(this.fields)) return null
      const field = this.fields.find(x => x && x.id === this.selectedFieldId)
      return field || null
    },

    /**
     * 取得選擇的案場列表
     */
    selectFieldList(): Field[] {
      if (!Array.isArray(this.dataField) || this.dataField.length === 0) {
        return []
      }
      return this.dataField
    },
  },

  actions: {
    /**
     * 取得 FormData
     */
    getFormData(object: Record<string, unknown>): FormData {
      const formData = new FormData()
      Object.keys(object).forEach(key => formData.append(key, object[key] as string))
      return formData
    },

    /**
     * 取得案場
     */
    getField(fieldId: string): Field | undefined {
      return this.fields.find(x => x.id === fieldId)
    },

    /**
     * 取得案場名稱
     */
    getFieldName(fieldId: string): string {
      return this.fields.find(x => x?.id === fieldId)?.name || ''
    },

    /**
     * 處理選擇變更（保留向後兼容）
     */
    handleSelectionChange(type: string, value: unknown): void {
      console.log('[View] handleSelectionChange 已移到 FieldSelector 組件:', type, value)
    },

    /**
     * 登出後清理
     */
    afterLogout(): void {
      this.setParam('orgId', null)
      this.setParam('fieldId', null)
      this.organizations = []
      this.fields = []
      this.relationships = { users: {}, organizations: {}, fields: {} }
      // 清除緩存
      localStorage.removeItem('cached_organizations')
    },

    /**
     * 更新 URL 搜尋參數（已禁用）
     */
    updateUrlSearch(): void {
      // URL 參數功能已禁用，只使用 localStorage
    },

    /**
     * 從 URL 初始化
     */
    initFromUrlSearch(): void {
      Object.entries(PARAM_CONFIG).forEach(([_key, config]) => {
        const stateKey = config.stateKey as keyof FieldStateType | null
        if (stateKey && (this as unknown as Record<string, unknown>)[stateKey] === undefined) {
          ;(this as unknown as Record<string, unknown>)[stateKey] = config.defaultValue
        }
      })
    },

    /**
     * 取得參數
     * 優先從當前 state 讀取，若為空則從 localStorage 讀取
     */
    getParam(paramKey: string): unknown {
      const config = PARAM_CONFIG[paramKey]
      if (!config) return null
      const stateKey = config.stateKey as keyof FieldStateType | null

      // 優先從當前 state 讀取
      if (stateKey) {
        const currentValue = (this as unknown as Record<string, unknown>)[stateKey]
        if (currentValue !== undefined && currentValue !== '') {
          return currentValue
        }
      }

      // 其次從 localStorage 讀取
      const storedValue = localStorage.getItem(config.storageKey)
      if (storedValue !== null && storedValue !== '') {
        // 同步更新 state
        if (stateKey) {
          ;(this as unknown as Record<string, unknown>)[stateKey] = storedValue
        }
        return storedValue
      }

      return config.defaultValue
    },

    /**
     * 設定參數
     */
    setParam(paramKey: string, value: unknown): void {
      const config = PARAM_CONFIG[paramKey]
      if (!config) return
      let processedValue = value
      if (typeof config.defaultValue === 'boolean') {
        processedValue = value === 'true' || value === true
      }
      const stateKey = config.stateKey as keyof FieldStateType | null
      if (stateKey) {
        ;(this as unknown as Record<string, unknown>)[stateKey] = processedValue
      }
      if (processedValue !== config.defaultValue) {
        localStorage.setItem(config.storageKey, String(processedValue))
      } else {
        localStorage.removeItem(config.storageKey)
      }
      this.updateUrlSearch()
    },

    /**
     * 切換組織
     */
    async changeOrg(org: string | Organization): Promise<void> {
      if (!org) return
      const orgId = typeof org === 'string' ? org : org.id
      const orgName = typeof org === 'string' ? '' : org.name

      this.dataIsLoaded = true
      this.setParam('orgId', orgId)
      if (orgName) this.selectedOrgName = orgName
      this.setParam('fieldId', '')

      // 保存到 Supabase（等待完成以確保頁面重載前保存成功）
      const user = authService.currentUser
      const userId = user?.uid || user?.id
      if (userId) {
        try {
          await authService.saveLastSelectedOrganization(userId, orgId)
        } catch (err) {
          console.error('[fieldScope] 保存組織選擇失敗:', err)
          // 即使保存失敗，localStorage 已經保存了，頁面重載後仍可從 localStorage 讀取
        }
      }
    },

    /**
     * 取得緩存的案場
     */
    getCachedFields(): Field[] | null {
      const cached = localStorage.getItem(CACHE_CONFIG.fields.key)
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached)
          if (data && Array.isArray(data) && data.length > 0) {
            if (Date.now() - timestamp < CACHE_CONFIG.fields.expiry) {
              const uniqueFields = Array.from(
                new Map(data.map((field: Field) => [field.id, field])).values()
              )
              console.log('從緩存讀取案場數據，去重後計', uniqueFields.length, '筆')
              return uniqueFields as Field[]
            }
          }
        } catch (e) {
          console.error('解析案場緩存時發生錯誤:', e)
          this.clearFieldsCache()
        }
      }
      return null
    },

    /**
     * 檢查是否有權限執行動作
     */
    canPerformAction(action: string): boolean | Promise<boolean> {
      const user = authService.currentUser as { uid?: string; id?: string } | null
      const userId = user?.uid || user?.id
      if (!userId) return false
      return accessService.canPerformAction(
        action,
        this.selectedOrgId,
        this.selectedFieldId,
        userId
      )
    },

    /**
     * 設定緩存的案場
     */
    setCachedFields(fields: Field[]): void {
      if (!Array.isArray(fields)) return
      try {
        const uniqueFields = Array.from(
          new Map(fields.map(field => [field.id, field])).values()
        )
        localStorage.setItem(CACHE_CONFIG.fields.key, JSON.stringify({
          data: uniqueFields,
          timestamp: Date.now()
        }))
      } catch (e) {
        console.error('設置案場緩存時發生錯誤:', e)
        this.clearFieldsCache()
      }
    },

    /**
     * 清除案場緩存
     */
    clearFieldsCache(): void {
      try {
        localStorage.removeItem(CACHE_CONFIG.fields.key)
      } catch (e) {
        console.error('清除案場緩存時發生錯誤:', e)
      }
    },

    /**
     * 更新城市列表
     */
    updateCityList(): void {
      const selectFilter = this.getSelectorState?.()?.selectFilter || this.selectFilter || '監控案場'
      const filterTypeKey = this.filterTypeList.find(ft => ft.title === selectFilter)?.key

      if (!Array.isArray(this.fields)) {
        this.dataFieldWithType = []
        this.dataCity = []
        this.dataField = []
        return
      }

      const focusFields = this.focusFields
      if (filterTypeKey === 'FOCUS' && focusFields?.length > 0) {
        this.dataFieldWithType = this.fields.filter(f => f && focusFields.includes(f.id))
      } else {
        this.dataFieldWithType = this.fields.filter(f => {
          if (!f) return false
          return selectFilter === '全部類型' || (f.type && f.type.includes(filterTypeKey || ''))
        })
      }

      const availableCities = new Set(
        this.dataFieldWithType
          .map(x => normalizeCity(x?.city) || '')
          .filter(city => city && city !== '' && city !== '---')
      )

      this.dataCity = ['全部地區']
      const cityObjects: CityOption[] = TAIWAN_CITIES.map(city => ({
        name: city,
        available: availableCities.has(city)
      }))

      availableCities.forEach(city => {
        if (!TAIWAN_CITIES.includes(city)) {
          cityObjects.push({ name: city, available: true })
        }
      })

      this.dataCity = (this.dataCity as (string | CityOption)[]).concat(cityObjects)
      this.applyInitialFilter()
      eventBus.emit('dataFieldWithType-updated')
      this.dataIsLoaded = true
    },

    /**
     * 套用過濾器（根據當前 state 的 selectFilter/selectCity 來過濾 dataField）
     */
    applyInitialFilter(): void {
      // 注意：不要在這裡呼叫 initSelectorState()，否則會覆蓋剛設定的值
      // initSelectorState() 只應在頁面初始化時呼叫
      const filterTypeKey = this.filterTypeList.find(ft => ft.title === this.selectFilter)?.key

      if (!Array.isArray(this.dataFieldWithType)) {
        this.dataField = []
        this.selectFieldsCount = 0
        return
      }

      const cityFilter = this.selectCity === '全部地區' ? '' : normalizeCity(this.selectCity)
      this.dataField = this.dataFieldWithType
        .filter(f => {
          if (!f) return false
          const normalizedCity = normalizeCity(f.city)
          const cityMatch = !cityFilter || (normalizedCity && normalizedCity.indexOf(cityFilter) >= 0)
          const keywordMatch = !this.searchKeyword ||
            (f.name?.includes(this.searchKeyword) || f.address?.includes(this.searchKeyword))
          const typeMatch = this.selectFilter === '全部類型' ||
            (f.type && Array.isArray(f.type) && f.type.includes(filterTypeKey || ''))
          return cityMatch && keywordMatch && typeMatch
        })
        .sort((a, b) => (a.name || '') > (b.name || '') ? 1 : -1)

      this.selectFieldsCount = this.dataField.length

      let totalLat = 0
      let totalLng = 0
      let validFields = 0
      this.dataField.forEach(field => {
        if (field?.location?.latitude && field?.location?.longitude) {
          const lat = parseFloat(String(field.location.latitude))
          const lng = parseFloat(String(field.location.longitude))
          if (!isNaN(lat) && !isNaN(lng)) {
            totalLat += lat
            totalLng += lng
            validFields++
          }
        }
      })

      if (validFields > 0) {
        this.mapLastTargetPoint = { lat: totalLat / validFields, lng: totalLng / validFields }
      }
    },

    /**
     * 初始化選擇器狀態
     */
    initSelectorState(): void {
      const urlParams = new URLSearchParams(window.location.search)
      const storedFilter = localStorage.getItem('view_selectFilter')
      const storedCity = localStorage.getItem('view_selectCity')
      const storedTarget = localStorage.getItem('view_selectTarget')
      const storedKeyword = localStorage.getItem('view_searchKeyword')

      this.selectFilter = urlParams.get('filter') || storedFilter || '監控案場'
      this.selectCity = urlParams.get('c') || storedCity || '全部地區'
      this.selectTarget = urlParams.get('t') || storedTarget || '全部案場'
      const keyword = urlParams.get('k')
      this.searchKeyword = keyword ? decodeURIComponent(keyword) : (storedKeyword || '')

      // 同步更新 selectFilterKey（從 title 轉換為 key）
      const filterType = this.filterTypeList.find(ft => ft.title === this.selectFilter)
      this.selectFilterKey = filterType?.key || 'MONITOR'
    },

    /**
     * 取得使用者名稱
     */
    getUserName(uid: string): string {
      if (this.userNameDict[uid]) return this.userNameDict[uid]
      this.getUserNameAsync(uid)
      return uid
    },

    /**
     * 非同步取得使用者名稱
     */
    async getUserNameAsync(uid: string): Promise<string> {
      if (this.userNameDict[uid]) return this.userNameDict[uid]
      try {
        const userProfile = await authService.getUserProfile(uid)
        const userName = userProfile.fullName || userProfile.displayName || userProfile.email || uid
        this.userNameDict[uid] = userName
        return userName
      } catch (error) {
        console.error(`無法取得使用者資料 ${uid}:`, error)
        this.userNameDict[uid] = uid
        return '---'
      }
    },

    /**
     * 標準化城市名稱
     */
    normalizeCity(cityName: string | null | undefined): string {
      return normalizeCity(cityName)
    },
  },
})

// ==================== Vue Plugin ====================

export default {
  install: (app: App): void => {
    const fs = fieldState()
    app.config.globalProperties.fieldScope = fs
    // 保留向後相容（稍後移除）
    app.config.globalProperties.view = fs
    app.config.globalProperties.vs = fs
    watch(() => fs.dynamicComponents, (components) => {
      components.forEach((component, name) => app.component(name, component))
    }, { deep: true })
  }
}
